/**
 * Searchable historical log viewer for one session.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { LogLevel, SessionLogEntry } from "@memes/shared";
import { devLogsApi } from "../../lib/api";
import { LogEntryDetail } from "../../components/dev/LogEntryDetail";
import { LogFilterBar } from "../../components/dev/LogFilterBar";
import { LogLine } from "../../components/dev/LogLine";

export const DevLogViewer: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [level, setLevel] = useState<LogLevel | "">("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [source, setSource] = useState<"" | "server" | "client">("");
  const [module, setModule] = useState("");
  const [entries, setEntries] = useState<SessionLogEntry[]>([]);
  const [selected, setSelected] = useState<SessionLogEntry | null>(null);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const load = useCallback(
    async (append: boolean) => {
      if (!sessionId) return;
      setLoading(true);
      setError(null);
      try {
        const nextOffset = append ? offset + entries.length : 0;
        const res = await devLogsApi.searchSession(sessionId, {
          q: debouncedQ || undefined,
          level: errorsOnly ? undefined : level || undefined,
          errorsOnly: errorsOnly || undefined,
          module: module || undefined,
          source: source || undefined,
          offset: nextOffset,
          limit: 200,
        });
        setEntries((prev) => (append ? [...prev, ...res.entries] : res.entries));
        setOffset(nextOffset);
        setTotal(res.total);
        setHasMore(res.hasMore);
      } catch {
        setError("Failed to load logs. Check Dev key on production.");
      } finally {
        setLoading(false);
      }
    },
    [sessionId, debouncedQ, level, errorsOnly, module, source, offset, entries.length],
  );

  useEffect(() => {
    setEntries([]);
    setOffset(0);
    setSelected(null);
    void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when filters change
  }, [sessionId, debouncedQ, level, errorsOnly, module, source]);

  const jumpToNextError = (): void => {
    const el = document.querySelector('[data-log-level="error"], [data-log-level="fatal"]');
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (!sessionId) return null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to="/dev/logs" className="text-indigo-600 hover:underline dark:text-indigo-400">
          ← Sessions
        </Link>
        <span className="font-mono text-xs text-gray-500">{sessionId}</span>
        <span className="text-gray-400">·</span>
        <span className="text-gray-500">{total} matching lines</span>
        {errorsOnly ? (
          <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700 dark:bg-red-950 dark:text-red-300">
            errors only
          </span>
        ) : null}
      </div>

      <div className="card sticky top-16 z-10 space-y-2 p-3">
        <LogFilterBar
          searchRef={searchRef}
          search={q}
          onSearchChange={setQ}
          searchPlaceholder="Search logs (press /)"
          errorsOnly={errorsOnly}
          onErrorsOnlyChange={(v) => {
            setErrorsOnly(v);
            if (v) setLevel("");
          }}
          showLevelSelect
          level={level}
          onLevelChange={(v) => {
            setLevel(v);
            if (v) setErrorsOnly(false);
          }}
          showSourceSelect
          source={source}
          onSourceChange={setSource}
          showModuleInput
          module={module}
          onModuleChange={setModule}
        >
          <button type="button" className="btn text-sm" onClick={jumpToNextError}>
            Next error
          </button>
        </LogFilterBar>
      </div>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <div className="card overflow-hidden bg-gray-950/5 dark:bg-black/30">
        <div className="max-h-[55vh] overflow-y-auto">
          {entries.map((entry, i) => (
            <LogLine
              key={`${entry.ts}-${i}`}
              entry={entry}
              selected={
                selected?.ts === entry.ts &&
                selected?.msg === entry.msg &&
                selected?.level === entry.level
              }
              onSelect={setSelected}
            />
          ))}
          {!entries.length && !loading ? (
            <p className="p-4 text-sm text-gray-500">No matching log lines.</p>
          ) : null}
        </div>
        {selected ? (
          <LogEntryDetail entry={selected} onClose={() => setSelected(null)} />
        ) : (
          <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-400 dark:border-gray-800">
            Click any line to view full details
          </p>
        )}
      </div>

      {hasMore ? (
        <button
          type="button"
          className="btn w-full"
          disabled={loading}
          onClick={() => void load(true)}
        >
          {loading ? "Loading…" : "Load more"}
        </button>
      ) : null}
    </div>
  );
};
